import type { ApiError as ApiErrorBody } from '@kontor/shared';

/**
 * Zugriff auf die Wirkia-API. Die Anmeldung läuft über ein HttpOnly-Cookie, das dieses Skript
 * nicht lesen kann. Schreibende Aufrufe senden den Header X-Kontor-Client (CSRF-Schutz).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['X-Kontor-Client'] = 'backoffice';

  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, credentials: 'same-origin', body: body !== undefined ? JSON.stringify(body) : undefined });
  } catch {
    throw new ApiError(0, 'network', 'Keine Verbindung zum Server.');
  }
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (json as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? 'Unbekannter Fehler.', err?.fields);
  }
  return json as T;
}

/** Eine Datei hochladen. Sie ist der Anfragekörper, der Name steht im Header X-Filename (siehe Anhänge und Mitarbeiterdateien der API). */
export async function apiUpload<T>(path: string, file: File, headers: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': file.type || 'application/octet-stream', 'X-Kontor-Client': 'backoffice', 'X-Filename': encodeURIComponent(file.name), ...headers },
      credentials: 'same-origin',
      body: file,
    });
  } catch {
    throw new ApiError(0, 'network', 'Keine Verbindung zum Server.');
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = (json as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? 'Unbekannter Fehler.', err?.fields);
  }
  return json as T;
}

export function queryString(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}
